import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { LOGIN_PAGE_LABEL, ROUTES } from 'shared-ui/utils/constants'

// Cache normalized paths to avoid repeated toLowerCase/replace calls
const normalizedAuthPaths = [
  ROUTES.waitListLanding,
  ROUTES.waitListWelcome,
  ROUTES.waitListLogIn,
  ROUTES.waitListOTP,
  ROUTES.waitListSuccess,
  ROUTES.waitListInvite,
  ROUTES.login,
  ROUTES.register,
  ROUTES.payment,
  ROUTES.splash,
  ROUTES.newPassword,
  '/carddetail',
  '/forgotpwd',
  '/verifysignup',
  '/otpVerification',
  '/pswUpdated',
  '/updatedpayment'
].map((p) => p.toLowerCase().replace(/\/$/, ''));

const normalizedEternalRoutes = [
  '/eternal/spiritualdashboard',
  '/eternal/userprofile'
].map((p) => p.toLowerCase());

function isAuthRoute(path) {
  if (!path) return false

  const normalizedPath = path.toLowerCase().split('?')[0].replace(/\/$/, '')

  const isEternalPath =
    normalizedPath.startsWith('/eternal') &&
    !normalizedEternalRoutes.includes(normalizedPath)

  return normalizedAuthPaths.includes(normalizedPath) || isEternalPath
}

export default isAuthRoute

// Enforce input maxlength even when the value is set via direct DOM assignment
export function enforceInputMaxLength() {
  if (typeof window === 'undefined') return
  if (window.__ENFORCE_INPUT_MAX_LENGTH_PATCHED) return

  const desc = Object.getOwnPropertyDescriptor(
    HTMLInputElement.prototype,
    'value'
  )
  if (!desc || typeof desc.set !== 'function') return

  const originalSet = desc.set
  const originalGet = desc.get

  Object.defineProperty(HTMLInputElement.prototype, 'value', {
    configurable: true,
    enumerable: desc.enumerable,
    get: function () {
      return originalGet.call(this)
    },
    set: function (val) {
      try {
        const ml = Number(this.maxLength)
        if (!Number.isNaN(ml) && ml > 0 && typeof val === 'string' && val.length > ml) {
          val = val.slice(0, ml)
        }
      } catch (e) {
        console.error(e)
      }
      return originalSet.call(this, val)
    }
  })

  window.__ENFORCE_INPUT_MAX_LENGTH_PATCHED = true
}

export function getLowerCase(str) {
  return typeof str === 'string' ? str.toLowerCase() : undefined
}

export const getDeviceId = () => localStorage.getItem('deviceId')

export const convertStringToLowerCase = (str = '') => {
  return str
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

export const extractErrorInfo = (error) => {
  let errorType = error?.errorType || 'unknown_error'
  let message = 'Something went wrong'

  try {
    if (error?.response) {
      const data = error.response.data

      if (typeof data === 'string' && data.startsWith('<!DOCTYPE html')) {
        return { errorType, message }
      }

      if (typeof data === 'object') {
        errorType = data?.error || errorType
        message = typeof data?.message === 'string' ? data.message : message
      } else if (typeof data === 'string') {
        message = data
      }
    } else if (typeof error?.message === 'string') {
      const msg = error.message.trim()

      if (msg.startsWith('{') && msg.endsWith('}')) {
        const parsed = JSON.parse(msg)
        errorType = parsed?.error || errorType
        message = parsed?.message || message
      } else if (!msg.startsWith('<!DOCTYPE html')) {
        message = msg || message
      }
    }
  } catch {
    message = 'Something went wrong'
  }

  return { errorType, message }
}

/**
 * Convert WebM audio to WAV format
 * ⚠️ WARNING: This is CPU-intensive and runs on the main thread!
 * For better performance, consider using useAudioWorker hook which runs this in a Web Worker
 * Example: const { convertWebMToWav } = useAudioWorker()
 */
export async function convertWebMToWav(webmBlob) {
  const arrayBuffer = await webmBlob.arrayBuffer()
  const audioContext = new AudioContext()
  const audioBuffer = await audioContext.decodeAudioData(arrayBuffer)

  const numOfChannels = audioBuffer.numberOfChannels
  const length = audioBuffer.length * numOfChannels * 2 + 44
  const buffer = new ArrayBuffer(length)
  const view = new DataView(buffer)

  function writeString(view, offset, string) {
    for (let i = 0; i < string.length; i++) {
      view.setUint8(offset + i, string.charCodeAt(i))
    }
  }

  let offset = 0

  // WAV header
  writeString(view, offset, 'RIFF')
  offset += 4
  view.setUint32(offset, 36 + audioBuffer.length * numOfChannels * 2, true)
  offset += 4
  writeString(view, offset, 'WAVE')
  offset += 4
  writeString(view, offset, 'fmt ')
  offset += 4
  view.setUint32(offset, 16, true)
  offset += 4
  view.setUint16(offset, 1, true)
  offset += 2
  view.setUint16(offset, numOfChannels, true)
  offset += 2
  view.setUint32(offset, audioBuffer.sampleRate, true)
  offset += 4
  view.setUint32(offset, audioBuffer.sampleRate * numOfChannels * 2, true)
  offset += 4
  view.setUint16(offset, numOfChannels * 2, true)
  offset += 2
  view.setUint16(offset, 16, true)
  offset += 2
  writeString(view, offset, 'data')
  offset += 4
  view.setUint32(offset, audioBuffer.length * numOfChannels * 2, true)
  offset += 4

  // Write PCM samples
  const channelData = []
  for (let i = 0; i < numOfChannels; i++) {
    channelData.push(audioBuffer.getChannelData(i))
  }

  for (let i = 0; i < audioBuffer.length; i++) {
    for (let channel = 0; channel < numOfChannels; channel++) {
      const sample = Math.max(-1, Math.min(1, channelData[channel][i]))
      view.setInt16(
        offset,
        sample < 0 ? sample * 0x8000 : sample * 0x7fff,
        true
      )
      offset += 2
    }
  }

  return new Blob([view], { type: 'audio/wav' })
}

const ua = navigator.userAgent || navigator.vendor || window.opera
const params = new URLSearchParams(window.location.search)

export const isAndroidApp =
  params.get('source') === 'ANDROID' ||
  (/Android/.test(ua) && /wv/.test(ua)) // "wv" means WebView

// function detectPlatform() {
//     // 1. Mobile Android WebView (Android app)
//     const isAndroidApp = /Android/.test(ua) && /wv/.test(ua) // "wv" means WebView

//     // 2. Mobile Browser (any mobile browser)
//     const isMobileBrowser =
//         /Mobi|Android|iPhone|iPad|iPod/i.test(ua) && !/wv/.test(ua)

//     // 3. Desktop (default fallback)
//     if (isAndroidApp) {
//         return 'Mobile App (Android WebView)'
//     } else if (isMobileBrowser) {
//         return 'Mobile Browser'
//     } else {
//         return 'Desktop Browser'
//     }
// }

export const formatRecordingTime = (sec) => {
  if (!sec || isNaN(sec)) return '00:00'
  const h = String(Math.floor(sec / 3600)).padStart(2, '0')
  const m = String(Math.floor((sec % 3600) / 60)).padStart(2, '0')
  const s = String(Math.floor(sec % 60)).padStart(2, '0')
  return h === '00' ? `${m}:${s}` : `${h}:${m}:${s}`
}

export const formatAudioTime = (seconds) => {
  if (!seconds || isNaN(seconds)) return '0:00'
  const hrs = Math.floor(seconds / 3600)
  const mins = Math.floor((seconds % 3600) / 60)
  const secs = Math.floor(seconds % 60)
  return hrs > 0
    ? `${hrs}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
    : `${mins}:${String(secs).padStart(2, '0')}`
}

export const dateConversion = (date) => {
  const d = new Date(date)
  if (isNaN(d)) {
    return null // or "Invalid date"
  }
  return d.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  })
}

export const formatDateByLanguage = (dateValue, langCode = 'en-GB') => {
  const d = new Date(dateValue)
  if (isNaN(d)) return null

  const locale = langCode || 'en-GB'

  try {
    return d.toLocaleDateString(locale, {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    })
  } catch (_) {
    return dateConversion(dateValue)
  }
}

export const today = new Date().toLocaleDateString('en-GB', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
}).replace(',', '');

export const parseError = (error) => {
  if (!error) return null
  let message = error?.message || (typeof error === 'string' ? error : null)
  if (!message && typeof error === 'object') {
    message = error?.error || error?.detail
  }
  if (typeof message !== 'string') return null

  const trimmed = message.trim()
  try {
    if (trimmed.startsWith('{')) {
      const parsed = JSON.parse(trimmed)
      if (parsed && typeof parsed === 'object') {
        const target = (parsed.error && typeof parsed.error === 'object') ? parsed.error : parsed
        return target?.message || (typeof target?.error === 'string' ? target?.error : null) || target?.detail || message
      }
    }
    return message
  } catch (e) {
    return message
  }
}

export const getImageOrientation = (src) => {
  return new Promise((resolve) => {
    const img = new Image()
    img.src = src

    img.onload = () => {
      if (img.width > img.height) resolve('landscape')
      else if (img.height > img.width) resolve('portrait')
      else resolve('square')
    }

    img.onerror = () => resolve('landscape') // safe fallback
  })
}

export const getLabel = (value) => {
  const trimmed = value?.trim()
  if (!trimmed) return LOGIN_PAGE_LABEL.MOBILE_EMAIL_LABEL // default label

  // Remove leading + or non-alphanumeric chars for number check
  const firstChar = trimmed?.replace(/^[^\w\d]*/, '')[0]

  if (!firstChar) return LOGIN_PAGE_LABEL.MOBILE_EMAIL_LABEL
  if (/\d/.test(firstChar)) return LOGIN_PAGE_LABEL.MOBILE_LABEL
  if (/[a-zA-Z]/.test(firstChar)) return LOGIN_PAGE_LABEL.EMAIL_LABEL

  return LOGIN_PAGE_LABEL.MOBILE_EMAIL_LABEL
}

export const validatePhone = (value) => {
  // Only digits, exactly 10 digits
  const phoneRegex = /^[0-9]{10}$/
  return phoneRegex.test(value)
}

export const validateEmail = (value) => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
}

export const getUserTimezone = () => {
  const offset = -new Date().getTimezoneOffset()
  const hours = Math.floor(offset / 60)
  const minutes = Math.abs(offset % 60)
  return `${hours}:${minutes.toString().padStart(2, '0')}`
}

export const truncateText = (str = '', limit = 35) => {
  if (!str) return ''
  return str.length > limit ? str.substring(0, limit) + '...' : str
}

export function getTimeAgo(dateString) {
  const now = new Date()

  let normalizedDate = dateString

  // Add Z only if timezone is missing
  if (!/[zZ]|[+-]\d{2}:\d{2}$/.test(dateString)) {
    normalizedDate = dateString + 'Z'
  }

  const created = new Date(normalizedDate.replace(/\.\d+/, ''))
  const diffMs = now - created

  if (isNaN(diffMs) || diffMs < 0) return 'Just now'

  const seconds = Math.floor(diffMs / 1000)
  const minutes = Math.floor(seconds / 60)
  const hours = Math.floor(minutes / 60)
  const days = Math.floor(hours / 24)

  if (seconds < 60) return 'Just now'
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`
  return `${days} day${days === 1 ? '' : 's'} ago`
}

export function lowercaseFirstWord(str) {
  if (!str) return str

  const [first, ...rest] = str.split(' ')
  let s = [first.toLowerCase(), ...rest].join(' ')
  return s.slice(0, -2)
}

export const getFirstWord = (str = '') => str.trim().split(/\s+/)[0] || ''

export const getCurrencySymbol = (currency) => {
  if (!currency || currency === 'None') return ''
  return currency === 'INR' ? '₹' : '$'
}

export const getPlanKeyName = (plan = {}) =>
  plan?.product_name || plan?.name || plan?.product_display_name || ''

export const isFreePlanName = (planOrName = '') => {
  const planName =
    typeof planOrName === 'string' ? planOrName : getPlanKeyName(planOrName)

  return getFirstWord(planName).toLowerCase() === 'free'
}

export const getPlanColor = (planOrName = '', fallbackColor = '') => {
  const planName =
    typeof planOrName === 'string' ? planOrName : getPlanKeyName(planOrName)

  if (!planName) return fallbackColor

  const colorMap = {
    free: '#666666',
    lite: '#88B422',
    basic: '#55A263',
    plus: '#2C9396',
    pro: '#0283CD'
  }

  return colorMap[getFirstWord(planName).toLowerCase()] || fallbackColor
}

// Cache for memoizing orientation calculations
const orientationCache = new Map()
const ORIENTATION_CACHE_MAX_SIZE = 500

export const getOrientation = ({ width, height }) => {
  if (width == null || height == null) return null

  const w = Number(width)
  const h = Number(height)

  if (Number.isNaN(w) || Number.isNaN(h)) return null
  if (w <= 0 || h <= 0) return null

  // Create cache key
  const cacheKey = `${w}x${h}`
  
  // Check cache first
  if (orientationCache.has(cacheKey)) {
    return orientationCache.get(cacheKey)
  }

  // Calculate orientation
  let orientation
  if (w < h) orientation = 'portrait'
  else if (w > h) orientation = 'landscape'
  else orientation = 'square'

  // Store in cache with size limit
  if (orientationCache.size >= ORIENTATION_CACHE_MAX_SIZE) {
    // Remove oldest entry (first key)
    const firstKey = orientationCache.keys().next().value
    orientationCache.delete(firstKey)
  }
  orientationCache.set(cacheKey, orientation)

  return orientation
}

export function AutoResizeText({
  text,
  minFontSize = 10,
  maxFontSize = 40,
  stepGranularity = 1,
  className,
  style,
  maxLines = 1
}) {
  const textRef = useRef(null)
  const numericMaxFontSize = typeof maxFontSize === 'number' ? maxFontSize : (parseFloat(String(maxFontSize).replace(/[^0-9.]/g, '')) || 40)
  const numericMinFontSize = typeof minFontSize === 'number' ? minFontSize : (parseFloat(String(minFontSize).replace(/[^0-9.]/g, '')) || 10)
  const [fontSize, setFontSize] = useState(numericMaxFontSize)

  const normalizedText = Array.isArray(text)
    ? text.map(t => (typeof t === 'string' || typeof t === 'number' ? t : (t?.props?.children || ''))).join('')
    : (typeof text === 'string' ? text : (text !== undefined && text !== null ? String(text) : ''))

  useEffect(() => {
    const element = textRef.current
    if (!element || !normalizedText) return

    const rafId = requestAnimationFrame(() => {
      if (!element) return

      const checkOverflow = () => {
        return (
          element.scrollWidth > element.clientWidth + 1 ||
          element.scrollHeight > element.clientHeight + 1
        )
      }

      // Check max font size first (fits in most cases, 0 loops needed)
      element.style.fontSize = `${numericMaxFontSize}px`
      if (!checkOverflow()) {
        setFontSize((prev) => (prev === numericMaxFontSize ? prev : numericMaxFontSize))
        return
      }

      // Check min font size
      element.style.fontSize = `${numericMinFontSize}px`
      if (checkOverflow()) {
        setFontSize((prev) => (prev === numericMinFontSize ? prev : numericMinFontSize))
        return
      }

      // Binary search between min and max font size
      let low = numericMinFontSize
      let high = numericMaxFontSize
      let best = numericMinFontSize

      while (low <= high) {
        const mid = Math.floor((low + high) / 2)
        element.style.fontSize = `${mid}px`

        if (checkOverflow()) {
          high = mid - stepGranularity
        } else {
          best = mid
          low = mid + stepGranularity
        }
      }

      element.style.fontSize = `${best}px`
      setFontSize((prev) => (prev === best ? prev : best))
    })

    return () => {
      cancelAnimationFrame(rafId)
    }
  }, [normalizedText, numericMinFontSize, numericMaxFontSize, stepGranularity, maxLines])

  return (
    <div
      ref={textRef}
      className={className}
      style={{
        fontSize: `${fontSize}px`,
        overflow: 'hidden',
        display: '-webkit-box',
        WebkitLineClamp: maxLines,
        WebkitBoxOrient: 'vertical',
        width: '100%',
        ...style
      }}
    >
      {normalizedText}
    </div>
  )
}

AutoResizeText.propTypes = {
  text: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.array, PropTypes.node]),
  minFontSize: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  maxFontSize: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  stepGranularity: PropTypes.number,
  className: PropTypes.string,
  style: PropTypes.object,
  maxLines: PropTypes.number
}



export const getEventTimestamp = () => {
  const date = new Date()
  const pad = (value) => String(value).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate()
  )} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(
    date.getSeconds()
  )}`
}

export const formatEventTimestamp = (timestamp) => {
  const date = new Date(timestamp)

  const pad = (value) => String(value).padStart(2, '0')

  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate()
  )} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(
    date.getSeconds()
  )}`
}

export const formatMonthYear = (dateString) => {
  if (!dateString) return ''

  const date = new Date(dateString)

  if (Number.isNaN(date.getTime())) return ''

  return date.toLocaleDateString('en-US', {
    month: 'short',
    year: 'numeric'
  })
}
export const formatCredits = (value) => {
    if (value >= 1_000_000) {
      return `${(value / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`
    }
    if (value >= 10_000) {
      return `${(value / 1_000).toFixed(1).replace(/\.0$/, '')}K`
    }
    return value
  }
export const isCreditError = (error) => {
  if (!error) return false

  // Direct error code check (e.g. AI003)
  const rawCode =
    error?.error_code ||
    error?.errorCode ||
    error?.code ||
    error?.response?.data?.error_code ||
    error?.response?.data?.error?.error_code ||
    error?.response?.data?.code ||
    error?.response?.data?.error?.code ||
    error?.raw?.error_code ||
    error?.raw?.code
  if (rawCode && String(rawCode).trim().toUpperCase() === 'AI003') {
    return true
  }

  // HTTP 402 Payment Required
  if (error?.status === 402 || error?.response?.status === 402) {
    return true
  }

  let str = ''
  if (typeof error === 'string') {
    str += ' ' + error
  }
  if (typeof error?.message === 'string') {
    str += ' ' + error.message
  }
  if (typeof error?.error === 'string') {
    str += ' ' + error.error
  }
  if (typeof error?.detail === 'string') {
    str += ' ' + error.detail
  }
  if (typeof error?.title === 'string') {
    str += ' ' + error.title
  }
  if (typeof error?.response?.data?.message === 'string') {
    str += ' ' + error.response.data.message
  }
  if (typeof error?.response?.data?.error === 'string') {
    str += ' ' + error.response.data.error
  }
  if (typeof error?.response?.data?.detail === 'string') {
    str += ' ' + error.response.data.detail
  }

  // Check if error or error.message is a JSON string
  try {
    const jsonCandidate =
      typeof error === 'string' && error.trim().startsWith('{')
        ? error.trim()
        : typeof error?.message === 'string' && error.message.trim().startsWith('{')
          ? error.message.trim()
          : null
    if (jsonCandidate) {
      const parsed = JSON.parse(jsonCandidate)
      const parsedObj = parsed?.error && typeof parsed.error === 'object' ? parsed.error : parsed
      const parsedCode =
        parsedObj?.error_code ||
        parsedObj?.errorCode ||
        parsedObj?.code
      if (parsedCode && String(parsedCode).trim().toUpperCase() === 'AI003') {
        return true
      }
      str += ' ' + (parsedObj?.message || '') + ' ' + (parsedObj?.error || '') + ' ' + (parsedObj?.detail || '')
    }
  } catch (e) {
    // ignore json parse error
  }

  const lower = str.toLowerCase()
  return (
    lower.includes('ai003') ||
    lower.includes('insufficient credit') ||
    lower.includes('insufficient credits') ||
    lower.includes('not enough credit') ||
    lower.includes('not enough credits') ||
    lower.includes('purchase more credit') ||
    lower.includes('purchase more credits')
  )
}